import { createConfig, fallback, http, type CreateConnectorFn } from 'wagmi'
import { bsc, bscTestnet } from 'wagmi/chains'
import type { Chain } from 'viem'
import { coinbaseWallet, injected, walletConnect } from 'wagmi/connectors'

/**
 * Wallet configuration.
 *
 * Three connection methods, in the order a BSC user is most likely to want them:
 *
 *   1. Injected      — MetaMask, Trust, Binance Wallet, OKX, and every in-app wallet
 *                      browser. No project ID, no relay, works offline-ish.
 *   2. WalletConnect — QR code on desktop, deep link on mobile. This is what lets
 *                      someone on a phone wallet use the desktop site.
 *   3. Coinbase      — its own SDK; also covers the Coinbase smart wallet.
 *
 * WalletConnect is only registered when a project ID is present. Registering it
 * without one throws at config construction and takes the whole app down, so an
 * unconfigured deployment silently falls back to injected + Coinbase rather than
 * showing a white screen.
 */

/**
 * Ordered BSC-testnet RPC pool.
 *
 * `VITE_BSC_TESTNET_RPC_URL` puts CrystalDEX's own provider endpoint first; it is
 * shipped in the client bundle, so use a key restricted to this origin. The public
 * endpoints remain available when the primary is throttled or temporarily unavailable.
 */
const CONFIGURED_TESTNET_RPC = import.meta.env.VITE_BSC_TESTNET_RPC_URL?.trim()

const PUBLIC_TESTNET_RPC_URLS = [
  'https://bsc-testnet-rpc.publicnode.com',
  'https://data-seed-prebsc-1-s1.bnbchain.org:8545',
] as const

export const BSC_TESTNET_RPC_URLS: readonly [string, ...string[]] = CONFIGURED_TESTNET_RPC
  ? [CONFIGURED_TESTNET_RPC, ...PUBLIC_TESTNET_RPC_URLS]
  : PUBLIC_TESTNET_RPC_URLS

export const BSC_MAINNET_RPC_URLS = [
  'https://bsc-rpc.publicnode.com',
  'https://bsc-dataseed.binance.org',
] as const

/**
 * Chain-97 metadata shared by wagmi reads and wallet_addEthereumChain.
 *
 * Keeping the override on this chain object preserves a clean boundary for adding
 * BSC mainnet or other chains later: each chain can carry its own independent pool.
 */
export const cdxBscTestnet = {
  ...bscTestnet,
  rpcUrls: {
    ...bscTestnet.rpcUrls,
    default: { http: BSC_TESTNET_RPC_URLS },
  },
} as const satisfies Chain

export const cdxBscMainnet = {
  ...bsc,
  rpcUrls: {
    ...bsc.rpcUrls,
    default: { http: BSC_MAINNET_RPC_URLS },
  },
} as const satisfies Chain

/**
 * CrystalDEX's home chain — where CDX, the bond market and the pool live.
 *
 * This pointed at BSC mainnet while the contracts were on a testnet, so any route
 * without a more specific requirement demanded a network with no contracts on it:
 * every balance and price would have read zero with no visible cause. It is derived
 * from the deployment now, so the two cannot drift apart again.
 *
 * Going to production is a one-line change here plus new addresses — see
 * `contractsChain` below.
 */
const environmentChainId = import.meta.env.PROD ? bsc.id : bscTestnet.id
const configuredChainId = Number(import.meta.env.VITE_CHAIN_ID ?? environmentChainId)
if (configuredChainId !== bsc.id && configuredChainId !== bscTestnet.id) {
  throw new Error('VITE_CHAIN_ID must be 56 (BSC mainnet) or 97 (BSC testnet)')
}
if (configuredChainId !== environmentChainId) {
  throw new Error(`VITE_CHAIN_ID must be ${environmentChainId} for this ${import.meta.env.PROD ? 'production' : 'development/test'} build`)
}
export const activeChain = configuredChainId === bsc.id ? cdxBscMainnet : cdxBscTestnet

const WC_PROJECT_ID = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID?.trim()

/** True when WalletConnect is usable. The UI reads this to explain why it's missing. */
export const walletConnectEnabled = Boolean(WC_PROJECT_ID)

const APP_METADATA = {
  name: 'CrystalDEX',
  description: 'Treasury-backed bCDX on BNB Smart Chain',
  url: typeof window !== 'undefined' ? window.location.origin : 'https://crystaldex.com',
  icons: [
    typeof window !== 'undefined' ? `${window.location.origin}/favicon.svg` : '',
  ].filter(Boolean),
}

function buildConnectors(): CreateConnectorFn[] {
  const connectors: CreateConnectorFn[] = [
    injected({ shimDisconnect: true }),
  ]

  if (WC_PROJECT_ID) {
    connectors.push(
      walletConnect({
        projectId: WC_PROJECT_ID,
        metadata: APP_METADATA,
        // We render our own connector picker, so suppress the bundled modal on
        // desktop. Mobile still needs it to hand off to the wallet app.
        showQrModal: true,
        qrModalOptions: { themeMode: 'dark' },
      }),
    )
  }

  connectors.push(
    coinbaseWallet({ appName: APP_METADATA.name, preference: 'all' }),
  )

  return connectors
}

/**
 * Chains.
 *
 * BSC is CrystalDEX's home chain — bonds, CDX, RBS and the treasury all live there,
 * and `activeChain` above is what the app guards against.
 *
 * Arbitrum is declared ONLY for the ported perpetuals terminal: Hyperliquid settles
 * deposits and withdrawals on Arbitrum One, so without it in this list the perps
 * code cannot switch to the chain it needs and every funding action fails.
 *
 * Declaring it here does not change Crystal's own behaviour — the wrong-network guard
 * still demands `activeChain`, and only the perps routes ever call
 * `switchChain({ chainId: arbitrum.id })`.
 */
/**
 * Where the CDX contracts live.
 *
 * Moved from Polygon Amoy to BNB Smart Chain testnet: Amoy has no DEX at all, so
 * the bond liquidity split had to run against an AMM we deployed ourselves. Chain
 * 97 has real PancakeSwap V2, which is the integration BSC mainnet will use — so
 * what is tested here is what ships.
 */
export const contractsChain = activeChain

/*
 * Every chain the app can touch, listed explicitly.
 *
 * This was `[activeChain, arbitrum, …]`, so pointing `activeChain` at the testnet
 * silently dropped BSC mainnet from the list — and the cross-chain bridge, which
 * genuinely needs mainnets, stopped type-checking. Naming them is duller and cannot
 * develop a hole when a constant moves.
 *
 *   bscTestnet  — CDX, the bond market and the PancakeSwap pool
 *   bsc         — bridge destination, and the production target
 *   arbitrum    — Hyperliquid settles perps here
 *   polygonAmoy — the previous deployment, kept so old links still resolve
 */
export const chains = configuredChainId === bsc.id
  ? [cdxBscMainnet] as const
  : [cdxBscTestnet] as const

export const wagmiConfig = createConfig({
  chains,
  connectors: buildConnectors(),
  transports: {
    // Chainstack is primary; viem advances to PublicNode when the primary is
    // throttled or unavailable. Both remain unbatched because BSC public RPCs do
    // not consistently accept transport-level JSON-RPC array requests.
    [cdxBscTestnet.id]: fallback(
      BSC_TESTNET_RPC_URLS.map((url) => http(url, {
        batch: false,
        retryCount: 1,
        timeout: 10_000,
      })),
      { retryCount: 1 },
    ),
    [cdxBscMainnet.id]: fallback(
      BSC_MAINNET_RPC_URLS.map((url) => http(url, {
        batch: false,
        retryCount: 1,
        timeout: 10_000,
      })),
      { retryCount: 1 },
    ),
  },
  // Client-rendered SPA — no SSR hydration to reconcile.
  ssr: false,
})

export const EXPLORER_URL =
  contractsChain.blockExplorers?.default.url ?? 'https://bscscan.com'

/** Hyperliquid settles here; only the perps routes need it. */
export const perpsChain = bscTestnet

/**
 * Which chain the CURRENT ROUTE requires.
 *
 * This exists to stop two prompts contradicting each other. The header's network
 * guard and the perps chain gate are separate components; without a shared answer,
 * a user on Arbitrum viewing /perps saw a red "Switch to BNB Smart Chain" button in
 * the header while the page beneath it worked perfectly — each component confidently
 * demanding a different network.
 */
/**
 * Compact chain names for UI chrome.
 *
 * `chain.name` is the formal label — "BNB Smart Chain Testnet", "Arbitrum One".
 * At 25 characters that turned the header's network control into the widest,
 * loudest element on the page. These are the names people actually say.
 */
export function shortChainName(id: number): string {
  switch (id) {
    case bscTestnet.id:
      return 'BNB Testnet'
    case bsc.id:
      return 'BNB Chain'
    default:
      return `Chain ${id}`
  }
}

/**
 * Everything a wallet needs in order to ADD a chain it does not already know.
 *
 * `wallet_switchEthereumChain` fails with 4902 when the wallet has never heard of
 * the target, which is the normal case for a testnet — few wallets ship one
 * preloaded. Handing wagmi these parameters lets it fall back to
 * `wallet_addEthereumChain` inside the same user gesture, so "Switch to BNB
 * Testnet" does what it says instead of failing silently.
 *
 * Every switch call site should spread this; a switch without it works only for
 * chains the user happens to have already.
 */
export function addChainParams(chain: Chain) {
  return {
    chainName: chain.name,
    nativeCurrency: chain.nativeCurrency,
    rpcUrls: [...chain.rpcUrls.default.http],
    blockExplorerUrls: chain.blockExplorers
      ? [chain.blockExplorers.default.url]
      : undefined,
  }
}

/**
 * Which chain the CURRENT ROUTE requires, or `null` when the route has no single
 * requirement.
 *
 *   /trade → Arbitrum  (Hyperliquid settles there)
 *   /swap  → null      (a cross-chain bridge; any supported chain is valid)
 *   else   → BSC       (bonds, CDX, RBS, treasury)
 *
 * `null` matters. Swap previously inherited the app-wide BSC requirement, so the
 * header demanded "Switch to BNB Chain" while the swap card beneath it was happily
 * bridging from Arbitrum — the page contradicting its own chrome. A bridge is the
 * one place where being on another chain is the normal case, not an error.
 */
export function requiredChainFor(_pathname: string) {
  return contractsChain
}

/** Display metadata for the connector picker, keyed by wagmi connector type. */
export const CONNECTOR_META: Record<string, { label: string; hint: string }> = {
  injected: {
    label: 'Browser wallet',
    hint: 'MetaMask, Trust, Binance Wallet, OKX — or any in-app wallet browser',
  },
  walletConnect: {
    label: 'WalletConnect',
    hint: 'Scan a QR code with a mobile wallet, or deep-link on phone',
  },
  coinbaseWallet: {
    label: 'Coinbase Wallet',
    hint: 'Coinbase extension, mobile app, or smart wallet',
  },
}
